import React from 'react'
import { useTranslation } from 'react-i18next'
import { buildBlobUrl } from '../../utils/blobUrl'
import { FALLBACK_IMAGE, applyFallbackIfNeeded } from '../../utils/imageFallback'

/**
 * VideoApprovalCard
 * Displays a single video in the approval queue
 *
 * Props:
 * - video: Video entity with video_id, file_name, thumbnail_blob_path, duration, file_size, blob_path, status
 * - onSelect: Callback when card is clicked to open modal
 * - t: Translation function
 */

function formatDuration(seconds) {
  if (!seconds) return '--:--'
  const mins = Math.floor(seconds / 60)
  const secs = Math.floor(seconds % 60)
  return `${mins}:${secs.toString().padStart(2, '0')}`
}

function formatFileSize(bytes) {
  if (!bytes) return '0 B'
  const units = ['B', 'KB', 'MB', 'GB']
  let size = bytes
  let unitIndex = 0

  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024
    unitIndex++
  }

  return `${size.toFixed(1)} ${units[unitIndex]}`
}

function formatDate(dateString) {
  if (!dateString) return '--'
  try {
    return new Date(dateString).toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric'
    })
  } catch {
    return '--'
  }
}

function VideoApprovalCard({ video, onSelect }) {
  const { t } = useTranslation('approval')

  // Determine thumbnail source (prefer blob_url from API, fallback to blob_path)
  const thumbnailSrc =
    video.blob_url ||  // SAS URL from API (preferred)
    video.thumbnail_url ||
    video.thumbnail_blob_path ||
    buildBlobUrl(video.blob_path) ||
    FALLBACK_IMAGE

  // DEBUG: Log video data
  console.log('[VideoApprovalCard] Video:', {
    video_id: video.video_id,
    blob_url: video.blob_url,
    blob_path: video.blob_path,
    thumbnailSrc
  })

  return (
    <div
      onClick={() => onSelect(video)}
      className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow duration-200 cursor-pointer"
    >
      {/* Video Thumbnail (9:16 aspect ratio like images) */}
      <div className="aspect-story bg-gray-100 relative group">
        <video
          src={thumbnailSrc}
          className="w-full h-full object-cover"
          muted
          playsInline
          preload="metadata"
          onError={applyFallbackIfNeeded}
        />

        {/* Type Badge - Red video badge (top-right) */}
        <div className="absolute top-2 right-2">
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold bg-red-600 text-white shadow">
            🎥 {t('page.grid.videoTypeBadge', 'Video')}
          </span>
        </div>

        {/* Play icon overlay on hover */}
        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-200 flex items-center justify-center">
          <svg
            className="h-12 w-12 text-white opacity-0 group-hover:opacity-70 transition-opacity duration-200"
            fill="currentColor"
            viewBox="0 0 24 24"
          >
            <path d="M8 5v14l11-7z" />
          </svg>
        </div>

        {/* Status Badge - Awaiting Approval (yellow) */}
        <div className="absolute bottom-2 left-2">
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 shadow">
            ⏳ {t('page.grid.awaitingApprovalBadge', 'Awaiting Approval')}
          </span>
        </div>
      </div>

      {/* Card Content */}
      <div className="p-4">
        {/* Filename */}
        <p className="text-sm font-medium text-gray-900 line-clamp-2 mb-2">
          {video.file_name}
        </p>

        {/* Video Metadata */}
        <div className="space-y-1 text-xs text-gray-600 mb-3">
          <div className="flex justify-between">
            <span>{t('page.grid.videoDuration', 'Duration')}:</span>
            <span className="font-semibold">{formatDuration(video.duration)}</span>
          </div>
          <div className="flex justify-between">
            <span>{t('page.grid.videoFileSize', 'File Size')}:</span>
            <span className="font-semibold">{formatFileSize(video.file_size)}</span>
          </div>
          {video.resolution && (
            <div className="flex justify-between">
              <span>{t('page.grid.videoResolution', 'Resolution')}:</span>
              <span className="font-semibold">{video.resolution}</span>
            </div>
          )}
          {video.created_at && (
            <div className="flex justify-between">
              <span>{t('page.grid.uploadedDate', 'Uploaded')}:</span>
              <span className="font-semibold">{formatDate(video.created_at)}</span>
            </div>
          )}
        </div>

        {/* Action Button */}
        <button
          onClick={(e) => {
            e.stopPropagation()
            onSelect(video)
          }}
          className="w-full btn-primary py-2 text-sm"
        >
          {t('page.grid.reviewButton', 'Review')}
        </button>
      </div>
    </div>
  )
}

export default VideoApprovalCard
